import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Chrome } from "lucide-react";
import logo from "@/assets/murphy-logo.png";

export default function Login() {
  const { login, loginWithGoogleSession } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@murphy.no");
  const [password, setPassword] = useState("Murphy2026!");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  useEffect(() => {
    const m = window.location.hash.match(/session_id=([^&]+)/);
    if (!m) return;
    const sid = decodeURIComponent(m[1]);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    setGoogleBusy(true);
    loginWithGoogleSession(sid)
      .then(() => nav("/"))
      .catch((err) => setError(apiError(err)))
      .finally(() => setGoogleBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(apiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen noise-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md relative z-10">
        <div className="mb-8">
          <img src={logo} alt="Murphy" className="h-9 w-auto brightness-0 invert" data-testid="login-logo" />
          <p className="text-muted-foreground text-xs mt-3">Control — fordi alt som kan gå galt, ikke trenger å gå galt.</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-8">
          <h1 className="font-display text-2xl font-medium mb-1">Logg inn</h1>
          <p className="text-muted-foreground text-sm mb-6">CRM & kundeonboarding for regnskapsforetak</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-caps">E-post</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-secondary border-border h-11" required />
            </div>
            <div>
              <Label className="label-caps">Passord</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-secondary border-border h-11" required />
            </div>
            {error && <p data-testid="login-error" className="text-destructive text-sm">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full h-11 bg-white text-black hover:bg-white/90 font-semibold">
              {loading ? "Logger inn…" : "Logg inn"}
            </Button>
          </form>
          <div className="mt-5 flex items-center gap-3">
            <div className="h-px bg-border flex-1" />
            <span className="text-xs text-muted-foreground">eller</span>
            <div className="h-px bg-border flex-1" />
          </div>
          <Button data-testid="login-google" type="button" variant="outline" onClick={startGoogle} disabled={googleBusy}
            className="w-full h-11 mt-4 bg-secondary border-border hover:bg-secondary/80">
            <Chrome className="w-4 h-4 mr-2" /> {googleBusy ? "Kobler til Google…" : "Logg inn med Google"}
          </Button>
          <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Demo: admin@murphy.no / Murphy2026! — flere roller: daglig@, oppdrag@, kyc@, salg@murphy.no</span>
          </div>
        </div>
      </div>
    </div>
  );
}
